import { escapeHtml } from '@shared/escape-html.utils';
import { buildPdfGroups, grandTotal, PdfGroup, PdfItem, PdfMeta } from './admin-evaluation-pdf.utils';

/** Estilos de la hoja imprimible. */
function printStyles(): string {
  return `<style>
    body{font-family:Arial,sans-serif;margin:16px;color:#111}
    h1{font-size:18px;margin:0 0 4px} p.meta{font-size:12px;margin:0 0 12px}
    table{width:100%;border-collapse:collapse;font-size:12px}
    th,td{border:1px solid #444;padding:4px 6px;text-align:left}
    th{background:#eee} td.num{text-align:right}
    td.vcat{width:34px;text-align:center;font-weight:bold;
      writing-mode:vertical-rl;transform:rotate(180deg);white-space:nowrap}
    tr.total td{font-weight:bold;background:#f2f2f2}
  </style>`;
}

/** Encabezado de la hoja imprimible. */
function printHead(meta: PdfMeta): string {
  return `<h1>Formulario de evaluación</h1><p class="meta">Módulo: ${escapeHtml(meta.module)} | Grupo: ${escapeHtml(meta.group)} | Año: ${meta.year}</p>`;
}

/** Celda vertical de categoría solo en la primera fila. */
function printCategoryCell(first: boolean, name: string, span: number): string {
  if (!first) return '';
  return `<td class="vcat" rowspan="${span}">${escapeHtml(name)}</td>`;
}

/** Fila de un criterio. */
function printItemRow(first: boolean, group: PdfGroup, item: PdfItem, span: number): string {
  const cat = printCategoryCell(first, group.name, span);
  if (isSubItem(item)) return printPlainRow(cat, item);
  return printRowCells(cat, item);
}

/** Indica si el criterio es subpregunta (índice con 3 partes). */
function isSubItem(item: PdfItem): boolean {
  return item.index.split('.').length > 2;
}

/** Fila sin negrita para subpreguntas. */
function printPlainRow(cat: string, item: PdfItem): string {
  return `<tr>${cat}<td>${item.index}</td><td>${escapeHtml(item.title)}</td><td class="num">${item.score}</td></tr>`;
}

/** Celdas de índice, criterio en negrita y puntaje en negrita. */
function printRowCells(cat: string, item: PdfItem): string {
  return `<tr>${cat}<td>${item.index}</td><td><strong>${escapeHtml(item.title)}</strong></td><td class="num"><strong>${item.score}</strong></td></tr>`;
}

/** Agrega la fila i-ésima del grupo. */
function addItemRow(html: string, group: PdfGroup, i: number, span: number): string {
  return html + printItemRow(i === 0, group, group.items[i], span);
}

/** Filas de criterios del grupo. */
function printItems(group: PdfGroup, span: number): string {
  let html = '';
  for (let i = 0; i < group.items.length; i++) html = addItemRow(html, group, i, span);
  return html;
}

/** Fila de total del grupo. */
function printGroupTotal(group: PdfGroup): string {
  return `<tr class="total"><td></td><td></td><td>TOTAL ${escapeHtml(group.name)}</td><td class="num">${group.total}</td></tr>`;
}

/** Bloque de un grupo con su total. */
function printGroup(group: PdfGroup): string {
  return printItems(group, group.items.length) + printGroupTotal(group);
}

/** Fila del total general. */
function printGrandTotal(groups: PdfGroup[]): string {
  return `<tr class="total"><td></td><td></td><td>TOTAL GENERAL</td><td class="num">${grandTotal(groups)}</td></tr>`;
}

/** Cuerpo de la tabla imprimible. */
function printBody(groups: PdfGroup[]): string {
  return printTableHead() + printTableFoot(groups);
}

/** Apertura de la tabla con cabecera. */
function printTableHead(): string {
  return `<table><thead><tr><th>Categoría</th><th>N°</th><th>Criterio</th><th>Puntaje</th></tr></thead><tbody>`;
}

/** Filas de grupos más total general y cierre. */
function printTableFoot(groups: PdfGroup[]): string {
  let html = '';
  for (const group of groups) html += printGroup(group);
  return html + printGrandTotal(groups) + '</tbody></table>';
}

/** Arma el documento HTML imprimible completo. */
export function buildPrintHtml(meta: PdfMeta, categories: any[]): string {
  const groups = buildPdfGroups(categories);
  return printDoc(printHead(meta), printBody(groups));
}

/** Envuelve cabecera y cuerpo en el documento. */
function printDoc(head: string, body: string): string {
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Formulario de evaluación</title>${printStyles()}</head><body>${head}${body}</body></html>`;
}

/** Abre la ventana de impresión. */
function openPrintPopup(): any {
  return window.open('', '_blank', 'width=1100,height=750');
}

/** Escribe el documento e invoca imprimir. */
function writePrintDoc(popup: any, html: string): void {
  popup.document.write(html);
  popup.document.close();
  popup.focus();
  popup.print();
}

/** Abre la vista imprimible del formulario (Guardar como PDF). */
export function printEvaluationForm(meta: PdfMeta, categories: any[]): void {
  const popup = openPrintPopup();
  if (!popup) return;
  writePrintDoc(popup, buildPrintHtml(meta, categories));
}
